import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Play, Loader2, AlertCircle } from 'lucide-react';
import ReactPlayer from 'react-player';
import { toast } from '@/hooks/use-toast';

interface ClipData {
  id: number;
  momentId: number;
  timestamp: string;
  label: string;
  status: 'queued' | 'processing' | 'done' | 'failed';
  durationSeconds: number | null;
  errorMessage: string | null;
  previewUrl: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

interface BundleData {
  id: number;
  status: 'queued' | 'building' | 'done' | 'failed';
  errorMessage: string | null;
  downloadUrl: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

interface ClipResponse {
  clips: ClipData[];
  bundle: BundleData | null;
}

interface ClipManagerProps {
  setId: number;
}

export function ClipManager({ setId }: ClipManagerProps) {
  const [previewClip, setPreviewClip] = useState<ClipData | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<ClipResponse>({
    queryKey: ['clips', setId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/sets/${setId}/clips`);
      if (!res.ok) throw new Error('Failed to fetch clips');
      return res.json();
    },
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      const hasActive = d.clips.some(c => c.status === 'queued' || c.status === 'processing');
      const bundleActive = d.bundle && (d.bundle.status === 'queued' || d.bundle.status === 'building');
      return hasActive || bundleActive ? 5000 : false;
    },
  });

  const bundleMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/sets/${setId}/bundles`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create bundle');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clips', setId] });
      toast({ title: 'Success', description: 'ZIP bundle job created' });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return (
      <Card className="bg-[#171717] border-white/10 p-6">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading clips...
        </div>
      </Card>
    );
  }

  const clips = data?.clips || [];
  const bundle = data?.bundle;

  if (clips.length === 0) {
    return null;
  }

  const doneCount = clips.filter(c => c.status === 'done').length;
  const canCreateBundle = doneCount > 0;

  function getStatusBadge(status: string) {
    switch (status) {
      case 'queued':
        return <Badge variant="secondary" className="bg-gray-600 text-gray-200">Queued</Badge>;
      case 'processing':
        return (
          <Badge variant="secondary" className="bg-amber-600/20 text-amber-400 border-amber-600/30">
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            Processing
          </Badge>
        );
      case 'done':
        return <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30">Done</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'building':
        return (
          <Badge variant="secondary" className="bg-amber-600/20 text-amber-400 border-amber-600/30">
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            Building
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  }

  return (
    <>
      <Card className="bg-[#171717] border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Clip Generator</h2>
            <p className="text-sm text-gray-400">{doneCount} of {clips.length} clips ready</p>
          </div>
          <Button
            onClick={() => bundleMutation.mutate()}
            disabled={!canCreateBundle || bundleMutation.isPending}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/10"
          >
            <Download className="h-4 w-4 mr-2" />
            {bundle?.status === 'done' && bundle.downloadUrl
              ? 'Regenerate ZIP'
              : 'Download All ZIP'}
          </Button>
        </div>

        {/* Bundle status */}
        {bundle && (
          <div className="mb-4 flex items-center gap-3">
            <span className="text-sm text-gray-400">ZIP Bundle:</span>
            {getStatusBadge(bundle.status)}
            {bundle.status === 'done' && bundle.downloadUrl && (
              <a
                href={bundle.downloadUrl}
                download
                className="text-sm text-white/60 hover:underline"
              >
                Download ZIP
              </a>
            )}
            {bundle.status === 'failed' && bundle.errorMessage && (
              <span className="text-sm text-red-400">{bundle.errorMessage}</span>
            )}
          </div>
        )}

        {/* Clip table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-gray-400">
                <th className="text-left py-2 pr-4">#</th>
                <th className="text-left py-2 pr-4">Timestamp</th>
                <th className="text-left py-2 pr-4">Note</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Preview</th>
              </tr>
            </thead>
            <tbody>
              {clips.map((clip, idx) => (
                <tr key={clip.id} className="border-b border-white/5">
                  <td className="py-3 pr-4 text-gray-400">{idx + 1}</td>
                  <td className="py-3 pr-4 font-mono text-white">{clip.timestamp}</td>
                  <td className="py-3 pr-4 text-white max-w-xs truncate">{clip.label}</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(clip.status)}
                      {clip.status === 'failed' && clip.errorMessage && (
                        <span title={clip.errorMessage}>
                          <AlertCircle className="h-4 w-4 text-red-400" />
                        </span>
                      )}
                    </div>
                    {clip.status === 'failed' && clip.errorMessage && (
                      <p className="mt-1 max-w-md text-xs leading-snug text-red-400/80 break-words">
                        {clip.errorMessage}
                      </p>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    {clip.status === 'done' && clip.previewUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreviewClip(clip)}
                        className="text-gray-400 hover:text-white"
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Preview dialog */}
      <Dialog open={!!previewClip} onOpenChange={() => setPreviewClip(null)}>
        <DialogContent className="bg-[#171717] border-white/10 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Clip Preview - {previewClip?.timestamp} - {previewClip?.label}
            </DialogTitle>
          </DialogHeader>
          {previewClip?.previewUrl && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <ReactPlayer
                url={previewClip.previewUrl}
                controls
                width="100%"
                height="100%"
                playing={false}
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload',
                      crossOrigin: 'anonymous',
                    },
                    forceVideo: true,
                  },
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
